import { create } from 'zustand';

export type ThemePref = 'system' | 'light' | 'dark';
export type MotionPref = 'system' | 'reduced';

export interface Toast {
  id: string;
  message: string;
  tone: 'neutral' | 'success' | 'danger';
  durationMs: number;
}

interface UiState {
  theme: ThemePref;
  motion: MotionPref;
  online: boolean;
  toasts: Toast[];
  setTheme: (t: ThemePref) => void;
  setMotion: (m: MotionPref) => void;
  setOnline: (online: boolean) => void;
  toast: (message: string, opts?: { tone?: Toast['tone']; durationMs?: number }) => string;
  dismissToast: (id: string) => void;
}

const THEME_KEY = 'audigoal.theme';
const MOTION_KEY = 'audigoal.motion';

/** localStorage can throw in private mode; never let that break boot. */
function readStored<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const v = localStorage.getItem(key);
    return v !== null && (allowed as readonly string[]).includes(v) ? (v as T) : fallback;
  } catch {
    return fallback;
  }
}

function persist(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* storage unavailable — the preference just will not survive a reload */
  }
}

/** Reflect preferences onto <html> so CSS (and the pre-paint script) can use them. */
export function applyThemeToDocument(theme: ThemePref): void {
  const el = document.documentElement;
  if (theme === 'system') delete el.dataset.theme;
  else el.dataset.theme = theme;
}

export function applyMotionToDocument(motion: MotionPref): void {
  const el = document.documentElement;
  if (motion === 'reduced') el.dataset.motion = 'reduced';
  else delete el.dataset.motion;
}

export const useUiStore = create<UiState>((set, get) => ({
  theme: readStored(THEME_KEY, ['system', 'light', 'dark'] as const, 'system'),
  motion: readStored(MOTION_KEY, ['system', 'reduced'] as const, 'system'),
  online: typeof navigator === 'undefined' ? true : navigator.onLine,
  toasts: [],

  setTheme: (theme) => {
    persist(THEME_KEY, theme);
    applyThemeToDocument(theme);
    set({ theme });
  },

  setMotion: (motion) => {
    persist(MOTION_KEY, motion);
    applyMotionToDocument(motion);
    set({ motion });
  },

  setOnline: (online) => set({ online }),

  toast: (message, opts) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const toast: Toast = {
      id,
      message,
      tone: opts?.tone ?? 'neutral',
      durationMs: opts?.durationMs ?? 4000,
    };
    set({ toasts: [...get().toasts, toast] });
    return id;
  },

  dismissToast: (id) => set({ toasts: get().toasts.filter((x) => x.id !== id) }),
}));

/** True when the user (or their OS) asked for less motion. */
export function prefersReducedMotion(): boolean {
  if (useUiStore.getState().motion === 'reduced') return true;
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
